import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '../../../../components/ui/Dialog';
import Input from '../../../../components/ui/Input';
import Button from '../../../../components/ui/Button';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';
import { useTranslation } from 'react-i18next';

interface BaseProps { loading: boolean; }

export interface AddVariablePayTypeDialogProps extends BaseProps {
  open: boolean;
  input: { name?: string; description?: string };
  onChange: (partial: any) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export const AddVariablePayTypeDialog: React.FC<AddVariablePayTypeDialogProps> = ({ open, input, onChange, onClose, onSubmit, loading }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="sm" data-testid="settings-form-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AppIcon name="PlusCircle" size={18} className="text-primary" />
            <DialogTitle>{t('payroll:Create Variable Pay Type')}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Input
              label={t('common:Name')}
              value={input.name || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ name: e.target.value })}
              autoFocus
            />
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('payroll:Description')}
              </label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 min-h-[4.5rem]"
                value={input.description || ''}
                onChange={(e) => onChange({ description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>{t('common:Cancel')}</Button>
          <Button onClick={onSubmit} disabled={loading} loading={loading} data-testid="save-btn">
            {t('common:Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export interface EditVariablePayTypeDialogProps extends BaseProps {
  open: boolean;
  input: { name?: string; description?: string };
  onChange: (partial: any) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export const EditVariablePayTypeDialog: React.FC<EditVariablePayTypeDialogProps> = ({ open, input, onChange, onClose, onSubmit, loading }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="sm" data-testid="settings-form-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AppIcon name="Pencil" size={18} className="text-primary" />
            <DialogTitle>{t('payroll:Edit Variable Pay Type')}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Input
              label={t('common:Name')}
              value={input.name || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ name: e.target.value })}
              autoFocus
            />
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('payroll:Description')}
              </label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 min-h-[4.5rem]"
                value={input.description || ''}
                onChange={(e) => onChange({ description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>{t('common:Cancel')}</Button>
          <Button onClick={onSubmit} disabled={loading} loading={loading} data-testid="save-btn">
            {t('common:Update')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export interface DeleteVariablePayTypeDialogProps extends BaseProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteVariablePayTypeDialog: React.FC<DeleteVariablePayTypeDialogProps> = ({ open, onClose, onConfirm, loading }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AppIcon name="Trash2" size={18} className="text-primary" />
            <DialogTitle>{t('payroll:Delete Variable Pay Type')}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">{t('common:Are you sure?')}</p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>{t('common:Cancel')}</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading} loading={loading}>
            {t('common:Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default {
  AddVariablePayTypeDialog,
  EditVariablePayTypeDialog,
  DeleteVariablePayTypeDialog
};
